import { Inspector } from "./entities/missionMap/missionMap";

/**
 * Uploads file/signature inspector messages and returns the inspectors
 * ready to be sent alongside an answer.
 */
export async function uploadInspectorMessages(
  inspectors: { [i: number]: Inspector },
  upload: (file: File) => { unwrap: () => Promise<unknown> },
): Promise<Inspector[]> {
  const ret: Inspector[] = [];
  for (const key in inspectors) {
    if (Object.hasOwn(inspectors, key)) {
      const inspector = inspectors[key];
      if (!inspector.message) continue;

      if (inspector.kind === "file") {
        for (const fileKey in inspector.message) {
          if (Object.hasOwn(inspector.message, fileKey)) {
            const url = await upload(inspector.message[fileKey]).unwrap();
            ret.push({
              ...inspector,
              message: url,
              title: inspector.message[fileKey]?.name,
            });
          }
        }
      } else if (inspector.kind === "signature") {
        const url = await upload(inspector.message).unwrap();
        ret.push({ ...inspector, message: url });
      } else {
        ret.push({ ...inspector, message: `${inspector.message}` });
      }
    }
  }

  return ret;
}

export function getMobileOperatingSystem(): "iOS" | "android" | null {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return null;
  }

  if (/android/i.test(userAgent)) {
    return "android";
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }

  return null;
}

export function parseWorkSessionIDs(json?: string): number[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getContrastYIQ(hexcolor: string) {
  const r = parseInt(hexcolor.substring(1, 3), 16);
  const g = parseInt(hexcolor.substring(3, 5), 16);
  const b = parseInt(hexcolor.substring(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#ffffff";
}

/**
 * Rewrites a Font Awesome class to its solid cut.
 *
 * Pipeline icons are typically duotone, which renders a second layer at low
 * opacity — fine on a button, too ghosted inside a small chip. Brand icons have
 * no solid cut, so they are left alone.
 *
 * @param {string} icon The interactor's icon class, e.g. "fad fa-hand-paper".
 * @returns {string | undefined} The same class with a solid style prefix.
 */
export function solidIcon(icon?: string): string | undefined {
  if (!icon) return icon;
  if (/\b(?:fab|fa-brands)\b/.test(icon)) return icon;
  return icon
    .replace(/\bfa[drlt]\b/g, "fas")
    .replace(/\bfa-(?:duotone|regular|light|thin)\b/g, "fa-solid");
}
